import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Target, Buildings, ChartBar, AirplaneTilt, Newspaper, BellRinging,
  ArrowRight, Star, Phone, WhatsappLogo, CheckCircle, Sparkle,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitLead } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { toast } from "sonner";

const FEATURES = [
  {
    icon: Target,
    accent: "#6043EF",
    tint: "bg-violet-50",
    title: "Rank & Cutoff Predictor",
    blurb: "Tell us your NEET rank, category and home state — we instantly show the colleges where you have a real shot at a seat this year.",
    points: [
      "See High / Medium / Low chances for each college",
      "Based on real past-year closing ranks and counselling rounds",
      "Works for MBBS, BDS and all quotas — All India & state",
    ],
    cta: "Predict my colleges",
    to: "/ug-neet/predict",
  },
  {
    icon: Buildings,
    accent: "#0EA5E9",
    tint: "bg-sky-50",
    title: "Browse Every Medical College",
    blurb: "One place to explore every MBBS & BDS college in India — with fees, seats, ratings and photos side by side.",
    points: [
      "Filter by state, fees, seats and government / private",
      "See annual fees, hostel, ratings and official website",
      "Compare colleges before you fill your choices",
    ],
    cta: "Browse colleges",
    to: "/colleges",
  },
  {
    icon: ChartBar,
    accent: "#EA580C",
    tint: "bg-orange-50",
    title: "Seat Matrix & Yearly Stats",
    blurb: "Understand how seats are changing — brand-new colleges, expanded intake, and how your state stacks up over the years.",
    points: [
      "See which colleges are new this year",
      "Track seat increases and decreases year over year",
      "Explore intake state by state",
    ],
    cta: "Open stats",
    to: "/stats",
  },
  {
    icon: AirplaneTilt,
    accent: "#10B981",
    tint: "bg-emerald-50",
    title: "Live Counselling Dashboard",
    blurb: "An airport-style board that shows exactly what's happening in counselling right now — what's open, what's closing today, and what's next.",
    points: [
      "Choice-filling & allotment windows at a glance",
      "Countdowns so you never miss a deadline",
      "Covers MCC, state and PG counselling",
    ],
    cta: "See what's live",
    to: "/counselling",
  },
  {
    icon: Newspaper,
    accent: "#4338CA",
    tint: "bg-indigo-50",
    title: "Central Newsroom",
    blurb: "Every official NEET notice — from NMC, MCC, NBE and NTA — gathered in one clean, always-up-to-date feed.",
    points: [
      "No more hunting across dozens of official websites",
      "Each notice links back to its original source",
      "Updated automatically as officials publish",
    ],
    cta: "Read the newsroom",
    to: "/#central-newsroom",
  },
  {
    icon: BellRinging,
    accent: "#DB2777",
    tint: "bg-pink-50",
    title: "Free News Alerts",
    blurb: "Subscribe once and get an email the moment a new notice for your state or for central counselling goes live.",
    points: [
      "Pick your state or central counselling",
      "One quick email confirmation, then you're set",
      "Completely free — no login needed",
    ],
    cta: "Get alerts",
    to: "/#central-newsroom",
  },
];

function FeatureCard({ f }) {
  const Icon = f.icon;
  return (
    <div
      data-testid={`about-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className="group relative bg-white border-2 border-zinc-200 rounded-2xl p-5 sm:p-6 hover:border-[#09090B] hover:-translate-y-1 transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.04)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,0.08)]"
    >
      <div className={`w-12 h-12 rounded-xl ${f.tint} flex items-center justify-center mb-4`}>
        <Icon size={24} weight="duotone" style={{ color: f.accent }} />
      </div>
      <h3 className="text-lg font-bold text-[#09090B] tracking-tight mb-1.5">{f.title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">{f.blurb}</p>
      <ul className="space-y-1.5 mb-5">
        {f.points.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-zinc-700">
            <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" style={{ color: f.accent }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Link
        to={f.to}
        data-testid={`about-cta-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
        className="inline-flex items-center gap-1.5 text-sm font-bold tracking-tight transition-colors"
        style={{ color: f.accent }}
      >
        {f.cta}
        <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}

function CallbackPanel() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rank, setRank] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.displayName || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || (!user && !name.trim())) {
      toast.error(user ? "Phone number is required" : "Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await submitLead({
        name: (name || user?.displayName || "").trim(),
        email: (email || user?.email || "").trim() || undefined,
        phone: phone.trim(),
        neet_rank: rank ? parseInt(rank, 10) : undefined,
        message: msg.trim() || undefined,
        lead_source: "about_page",
      });
      setDone(true);
      toast.success("We'll call you back within 24 hours");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const waLink = () => {
    const text = encodeURIComponent(
      `Hi, I'd like guidance on NEET counselling. My NEET rank is ${rank || "____"}.`,
    );
    return `https://wa.me/919986143554?text=${text}`;
  };

  return (
    <div className="lg:sticky lg:top-24" data-testid="about-callback-panel">
      <div className="bg-white border-2 border-zinc-200 rounded-2xl overflow-hidden shadow-[6px_6px_0px_0px_rgba(0,0,0,0.06)]">
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-5 text-white">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-white/25 backdrop-blur-sm flex items-center justify-center rounded-lg shrink-0">
              <Star size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight">Get Priority Callback</h3>
              <p className="text-white/85 text-xs mt-1">Free 1-on-1 counselling guidance for your rank</p>
            </div>
          </div>
        </div>

        {done ? (
          <div className="p-7 text-center space-y-3" data-testid="about-callback-success">
            <CheckCircle size={42} weight="fill" className="text-emerald-500 mx-auto" />
            <div className="text-lg font-bold text-[#09090B]">Request received!</div>
            <p className="text-sm text-zinc-600">
              Our counselling team will call you at{" "}
              <span className="font-mono font-bold text-[#09090B]">{phone}</span> within 24 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            {!user && (
              <div>
                <Label htmlFor="ab-name" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Your Name *</Label>
                <Input id="ab-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Full name" data-testid="about-callback-name" required className="mt-1" />
              </div>
            )}
            {!user && (
              <div>
                <Label htmlFor="ab-email" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                <Input id="ab-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="about-callback-email" className="mt-1" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ab-phone" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Phone *</Label>
                <Input id="ab-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit mobile" data-testid="about-callback-phone" required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="ab-rank" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">NEET Rank</Label>
                <Input id="ab-rank" type="number" min="1" value={rank} onChange={(e) => setRank(e.target.value)}
                  placeholder="e.g. 15000" data-testid="about-callback-rank" className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="ab-msg" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Message (optional)</Label>
              <Textarea id="ab-msg" value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="What would you like help with?" data-testid="about-callback-message"
                rows={2} className="mt-1 resize-none" />
            </div>
            <button type="submit" disabled={busy}
              data-testid="about-callback-submit"
              className="w-full h-11 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60 transition-all shadow-[3px_3px_0px_0px_#09090B] rounded-md">
              <Phone size={16} weight="fill" />
              {busy ? "Submitting…" : "Request Callback"}
            </button>
            <a href={waLink()} target="_blank" rel="noopener noreferrer"
              data-testid="about-callback-whatsapp"
              className="w-full h-11 bg-[#25D366] hover:bg-[#20b957] text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors rounded-md">
              <WhatsappLogo size={18} weight="fill" />
              Chat on WhatsApp
            </a>
            <p className="text-[10px] text-center text-zinc-400 pt-1">
              <Star size={9} weight="fill" className="inline text-zinc-400 -mt-0.5" /> No spam — we only call to help you choose.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero — text left, priority callback top-right */}
      <div className="bg-white border-b border-zinc-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-[#6043EF] text-[11px] font-bold uppercase tracking-[0.2em] mb-5">
                <Sparkle size={13} weight="fill" className="text-[#6043EF]" />
                About Unicators
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#09090B] leading-[1.05]">
                Your entire NEET counselling journey, in one friendly place.
              </h1>
              <p className="text-base sm:text-lg text-zinc-600 mt-5 max-w-xl leading-relaxed">
                Unicators is a free platform that helps NEET aspirants figure out which medical & dental
                colleges they can get into, track live counselling, and stay on top of every official
                notice — without the confusion of jumping between a dozen government websites.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/ug-neet/predict" data-testid="about-hero-predict"
                  className="h-11 px-6 bg-[#6043EF] hover:bg-[#4f35d4] text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2 rounded-full transition-colors">
                  <Target size={16} weight="fill" /> Predict my colleges
                </Link>
                <Link to="/colleges" data-testid="about-hero-colleges"
                  className="h-11 px-6 bg-zinc-100 hover:bg-zinc-200 text-[#09090B] font-bold text-sm uppercase tracking-wider flex items-center gap-2 rounded-full transition-colors">
                  <Buildings size={16} weight="fill" /> Browse colleges
                </Link>
              </div>
            </div>
            <CallbackPanel />
          </div>
        </div>
      </div>

      {/* Body: features full width */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#09090B] mb-2">
          What you can do here
        </h2>
        <p className="text-sm text-zinc-500 mb-7 max-w-xl">
          Six free tools built for every NEET aspirant across India. Pick whatever helps you today.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => <FeatureCard key={f.title} f={f} />)}
        </div>
      </div>
    </div>
  );
}
